import { Link } from 'react-router';
import { CouncilClaimIntro } from '@/components/councils/council-claim-intro';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bristol\'s AMBER DfT rating mean I can claim?',
    answer: 'An AMBER overall rating does not guarantee a successful claim, but it reflects mixed performance across condition, spend and best practice. Bristol is AMBER on condition and best practice, GREEN on spend. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not the headline scorecard alone.',
  },
  {
    question: 'Bristol says it has no backlog of defects — what does that mean for my claim?',
    answer: 'The council states it does not operate a backlog and that repairs are undertaken within contractual timescales monitored through KPIs. That describes reported-defect workflow, not network-wide condition. Its own Gaist survey data shows A-road green condition fell from 28.9% to 21.5% (2020–2024), B-road green from 14.4% to 9.8%, and 85.7% of U-roads in fair or poor condition in 2024. Prior reports and photos of your specific defect matter more than the backlog statement.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 952km — 79% of Bristol\'s 1,209km carriageway network. Gaist condition surveys cover U-roads annually, but only 14.3% were green-rated in 2024 while 70.9% were amber and 14.8% red. Most pothole damage happens on these residential streets, not the 128km of A-roads.',
  },
  {
    question: 'Bristol has a GREEN spend rating — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because Bristol invests beyond its DfT capital allocation. Total highway maintenance capital and revenue spend rose from £11.2m (2020/21) to £23.9m (2024/25). Section 58 turns on the specific defect and inspection record — aggregate spend does not prove your pothole was known and repaired in time.',
  },
  {
    question: 'Pothole repairs rose 65% in five years — does that help my case?',
    answer: 'It can. Bristol filled 2,658 potholes in 2020/21 and 4,398 in 2024/25 — a 65.5% rise. Pothole repairs account for approximately 37% of reactive maintenance job types, according to the council. Rising patch counts alongside declining condition scores on A, B and C roads suggest reactive workload is growing — but your claim still needs evidence about the specific defect.',
  },
  {
    question: 'What does Bristol\'s "best value for money" statement mean?',
    answer: 'The council\'s 2025/26 plan states that where preventative maintenance is not the best engineering solution, it will "continue to use that asset for as long as possible ensuring best value for money whilst ensuring safety of the public." That documents a cost-conscious approach to keeping assets in service. Whether that affects your claim depends on your road, defect age and inspection records — not the quote alone.',
  },
  {
    question: 'How do I report a pothole to Bristol City Council?',
    answer: 'Report potholes and road damage via FixMyStreet at fixmystreet.bristol.gov.uk or through the council\'s road maintenance form at bristol.gov.uk. For urgent safety risks, call 0117 922 2100 (Mon–Fri 8.30am–6pm) or 0117 922 2050 at other times. Prior reports of the same defect strengthen a claim by demonstrating notice. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BristolCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bristol Pothole Claims: Council Form & Evidence | Fixtyer"
        description="Find Bristol City Council’s official highways claim form, the evidence to prepare and a free pothole claim letter template. Includes local road-maintenance data."
        canonical="/councils/bristol"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bristol', url: '/councils/bristol' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bristol City Council"
        councilSlug="bristol"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bristol"
        headline="Bristol Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bristol</span>
          </nav>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Bristol pothole claims: how to claim
          </h1>
          <CouncilClaimIntro area="Bristol" />
          <h2 className="font-display text-2xl font-bold mb-5">Bristol road maintenance: the evidence</h2>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bristol City Council maintains <strong className="text-white">1,209km</strong> of roads —{' '}
            <strong className="text-rose-400">952km of unclassified residential streets (79%)</strong>. The DfT rates
            Bristol <strong className="text-amber-400">AMBER</strong> overall with{' '}
            <strong className="text-emerald-400">GREEN</strong> spend, yet pothole fills rose{' '}
            <strong className="text-rose-400">65%</strong> from 2020/21 to 2024/25 while green-rated A-roads fell
            from 28.9% to 21.5% and B-roads to just 9.8%. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">9.8%</div>
            <div className="text-white font-medium mt-2">B roads in green condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 14.4% in 2020. A-roads lost green share too (28.9% → 21.5%). U-roads: only 14.3% green,
              85.7% fair or poor — on the roads where most claims start.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,209km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Bristol's June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,209km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">952km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">79% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">128km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">128km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <QuoteCard
            quote="Our authority is responsible for managing and maintaining a comprehensive highways network to ensure safe and efficient travel for all road users. This network includes 1,209 km of roads, encompassing 128 km of A roads, 128 km of B and C roads, and 952 km of U roads."
            source="Bristol City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Gaist Solutions Limited condition surveys — mapped to red, amber and green categories
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Bristol uses Gaist condition assessment surveys annually. Grades 1–2
              map to green, 3–4 to amber and 5 to red — a translation from Gaist's 1–5 grading, not traditional SCANNER
              laser survey. Survey data is collected at a specific point each year and may not reflect condition at the
              time of your incident.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (128km) — green share falling</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8.6%</td>
                    <td className="px-4 py-2 text-center">62.3%</td>
                    <td className="px-4 py-2 text-center">28.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">9.0%</td>
                    <td className="px-4 py-2 text-center">64.4%</td>
                    <td className="px-4 py-2 text-center">26.2%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9.9%</td>
                    <td className="px-4 py-2 text-center font-bold">68.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">21.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Green-rated A-roads fell from 28.9% to 21.5% (2020–2024). Red condition rose from 8.6% to 9.9%.
            </p>
          </div>

          {/* B roads */}
          <div className="bg-white border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B roads — only 9.8% green in 2024</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">10.5%</td>
                    <td className="px-4 py-2 text-center">75.0%</td>
                    <td className="px-4 py-2 text-center">14.4%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12.4%</td>
                    <td className="px-4 py-2 text-center font-bold">77.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B-road green condition fell from 14.4% to 9.8% over four years. Red rose from 10.5% to 12.4%.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (952km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">15.2%</td>
                    <td className="px-4 py-2 text-center">66.9%</td>
                    <td className="px-4 py-2 text-center">15.7%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">14.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">70.9%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">14.3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              In 2024, 85.7% of Bristol's residential network was fair or poor (amber plus red). Green U-road share
              barely moved — from 15.7% to 14.3% — while amber rose from 66.9% to 70.9%.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — defects requiring a patch to make the road safe, per the council's report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Bristol estimated potholes filled 2020–2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,658</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,464</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−7.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,587</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−2.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center text-amber-600">3,832</td>
                  <td className="px-6 py-4 text-center text-amber-600">+44.2%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,398</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+65.5%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">70% surge in two years</h3>
              <p className="text-surface-600 text-sm">
                Between 2022/23 and 2024/25 alone, pothole fills rose from 2,587 to 4,398 — a 70% increase. The council
                states pothole repairs account for approximately 37% of reactive maintenance job types annually.
              </p>
            </div>
            <QuoteCard
              quote="Bristol City Council does not operate a backlog of defects and or potholes. Any necessary repairs are undertaken within specified contractual timescales, which are monitored through KPIs."
              source="Bristol City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend scorecard — but much of the increase went to structures and lighting, not carriageways
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bristol highway maintenance spending 2020–2025</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Carriageway total (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">8,028</td>
                  <td className="px-4 py-3 text-center">3,175</td>
                  <td className="px-4 py-3 text-center">5,770</td>
                  <td className="px-4 py-3 text-center">66.0%</td>
                  <td className="px-4 py-3 text-center">34.0%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">9,134</td>
                  <td className="px-4 py-3 text-center">8,980</td>
                  <td className="px-4 py-3 text-center">4,499</td>
                  <td className="px-4 py-3 text-center">76.3%</td>
                  <td className="px-4 py-3 text-center">23.7%</td>
                </tr>
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">17,062</td>
                  <td className="px-4 py-3 text-center">6,862</td>
                  <td className="px-4 py-3 text-center">6,205</td>
                  <td className="px-4 py-3 text-center text-emerald-400">77.8%</td>
                  <td className="px-4 py-3 text-center">22.2%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Total capital and revenue spend rose from £11.2m (2020/21) to £23.9m (2024/25). Preventative share
                climbed to 77.84% in 2024/25. Structures capital alone reached £7.8m — up from £673k in 2020/21 —
                including CRSTS bridge works and New Cut River wall investment.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why carriageway outcomes lag</h3>
              <p className="text-brand-200 text-sm">
                Carriageway capital plus revenue rose only from £5.8m to £6.2m over the same period (+7.5%). The council
                resurfaces approximately 20,000m² of carriageway per year on a 1,209km network — alongside ~200,000m²
                of surface dressing. Rising pothole counts alongside flat carriageway spend suggest reactive pressure persists.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Bristol Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim statements from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <QuoteCard
              quote="In situations where preventative maintenance if not the best engineering solution we will seek to continue to use that asset for as long as possible ensuring best value for money whilst ensuring safety of the public."
              source="Bristol City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="Pothole repairs, which account for approximately 37% of reactive maintenance job types annually."
              source="Bristol City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bristol invests heavily in structures and lighting — your claim turns on the specific defect
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN DfT spend scorecard with £23.9m total maintenance spend in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 77.84% preventative maintenance share in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual Gaist condition surveys across all road classes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management using Gaist lifecycle modelling and ISO55000-aligned SAMP in development
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> NHT survey satisfaction above average, per the council's report
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall, condition and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,398 pothole fills in 2024/25 — up 65.5% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road green share down from 28.9% to 21.5%; B-roads to 9.8%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 952km of U-roads — 85.7% fair or poor in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Carriageway spend rose only 7.5% while total spend doubled
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Documented "best value for money" approach to keeping assets in service
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and annual Gaist surveys, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council portal) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, 85.7% fair/poor network condition supports structural context</li>
              <li>• Whether the council can show inspection and repair records for your street, not just network averages</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Bristol's own
              transparency data where it helps you — without overstating what aggregate scorecards prove.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Bristol City Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack.
          </p>
          <a
            href="https://fixmystreet.bristol.gov.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report on FixMyStreet — fixmystreet.bristol.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bristol?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Rising patch counts and declining condition data demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 65% pothole trend cited</li>
                <li>• No prior-report search</li>
                <li>• No carriageway spend context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 4,398 pothole repairs in 2024/25 documented</li>
                <li>✅ A/B/U-road condition decline cited</li>
                <li>✅ Carriageway vs structures spend split</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bristol</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bristol"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bristol Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bristol.gov.uk/files/documents/9750-local-highways-maintenance-transparency-report/file"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bristol City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
